import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  isSeatlessOrg,
  type AiQuotaPaywallReason,
} from "@superblocksteam/shared";

import { GauInText } from "core/billing";
import { useOrgAdminContact } from "core/billing/orgAdminContact";
import { Modal } from "core/components/Modal";
import { getCurrentUser } from "legacy/selectors/usersSelectors";
import {
  selectOnlyOrganization,
  selectOnlyOrganizationId,
} from "store/slices/organizations";
import { useListBuilderSeatsQuery } from "store/slices/reduxApi/builderSeats";

type ContactAdminBillingModalProps = {
  open: boolean;
  onClose: () => void;
  currentUserHasBuilderSeat?: boolean | null;
  reason?: AiQuotaPaywallReason;
};

export function ContactAdminBillingModal({
  open,
  onClose,
  currentUserHasBuilderSeat: currentUserHasBuilderSeatProp,
  reason,
}: ContactAdminBillingModalProps) {
  const organizationId = useSelector(selectOnlyOrganizationId);
  const organization = useSelector(selectOnlyOrganization);
  const currentUser = useSelector(getCurrentUser);
  // Seatless (credits-pricing) orgs have no seat concept: skip the seat
  // fetch and never show "Request AI Builder seat" copy — the GAU-adjustment
  // variant is the only one that makes sense.
  const isSeatless = isSeatlessOrg(
    organization?.billing?.pricingModel,
    organization?.billing?.plan,
  );
  const adminContact = useOrgAdminContact(organizationId, open);
  const { data: builderSeatsData } = useListBuilderSeatsQuery(undefined, {
    skip: !open || isSeatless,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const adminEmail =
    adminContact.status === "found" ? adminContact.email : null;
  const isAdminLoading =
    adminContact.status === "idle" || adminContact.status === "loading";
  const currentUserHasBuilderSeat = useMemo(() => {
    if (isSeatless) {
      return true;
    }
    if (typeof currentUserHasBuilderSeatProp === "boolean") {
      return currentUserHasBuilderSeatProp;
    }

    if (!currentUser || !builderSeatsData?.builders) {
      return null;
    }

    const normalizedEmail = currentUser.email.trim().toLowerCase();
    return builderSeatsData.builders.some(
      (builder) =>
        builder.userId === currentUser.id ||
        builder.email.trim().toLowerCase() === normalizedEmail,
    );
  }, [
    builderSeatsData?.builders,
    currentUser,
    currentUserHasBuilderSeatProp,
    isSeatless,
  ]);
  const isDeployQuota = reason === "deploy_quota_exceeded";
  const isPastDue = reason === "payment_past_due";
  const modalTitle = isPastDue ? (
    "Subscription payment failed"
  ) : isDeployQuota ? (
    "Deploy limit reached"
  ) : currentUserHasBuilderSeat ? (
    <GauInText>Request GAU adjustment</GauInText>
  ) : (
    "Request AI Builder seat"
  );
  const modalBody = isPastDue ? (
    "Your organization's most recent subscription payment didn't go through, so paid features are paused. You don't have permission to manage billing — ask an organization admin to update the payment method and pay the outstanding invoice."
  ) : isDeployQuota ? (
    "You do not have permission to manage billing for your organization. Ask an organization admin to add more published apps."
  ) : currentUserHasBuilderSeat ? (
    <GauInText>
      You do not have permission to manage billing for your organization. Ask an
      organization admin to adjust GAUs for you.
    </GauInText>
  ) : (
    "You do not have permission to manage billing for your organization. Ask an organization admin to assign you an AI Builder seat"
  );
  const contactAdminSubject = isPastDue
    ? "Subscription payment failed — billing action needed"
    : isDeployQuota
      ? "Request additional published apps"
      : currentUserHasBuilderSeat
        ? "Request GAU adjustment"
        : "Request AI Builder seat or GAU adjustment";
  const contactAdminBody = isPastDue
    ? "Hi,\n\nOur Superblocks subscription is currently past due — the most recent payment didn't go through and AI features are paused. Could you open the Stripe billing portal to update the payment method and pay the outstanding invoice?\n\nThanks."
    : isDeployQuota
      ? "Hi,\n\nCould you please add more published apps to our plan?\n\nThanks."
      : currentUserHasBuilderSeat
        ? "Hi,\n\nCould you please adjust our GAUs for me?\n\nThanks."
        : "Hi,\n\nCould you please purchase AI Builder seats or adjust our GAUs for me?\n\nThanks.";

  // Synchronous so the browser treats the mailto as part of the click. With
  // no admin found the recipient is left blank for the user to fill in.
  const openContactAdmin = () => {
    if (isAdminLoading) return;
    const mailto = `mailto:${encodeURIComponent(adminEmail ?? "")}?subject=${encodeURIComponent(contactAdminSubject)}&body=${encodeURIComponent(contactAdminBody)}`;
    window.open(mailto, "_blank", "noreferrer");
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      className="w-120 gap-0 rounded-lg"
      titleClassName="h-12 py-0 pl-6 text-base"
      bodyClassName="px-6 py-5"
      footerClassName="items-center border-t border-sb-grey-100 px-6 py-4"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 transition hover:bg-gray-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={openContactAdmin}
            disabled={isAdminLoading}
            data-test="contact-admin-button"
            className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-md bg-black px-5 text-sm font-medium !text-white transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isAdminLoading ? (
              <span className="size-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            ) : null}
            Contact admin
          </button>
        </>
      }
    >
      <p className="m-0 text-sm leading-6 text-sb-grey-700">{modalBody}</p>
      {isAdminLoading ||
      adminContact.status === "none" ||
      adminContact.status === "error" ? (
        <p className="m-0 mt-3 text-sm leading-6 text-sb-grey-500">
          {isAdminLoading
            ? "Finding your organization admin..."
            : adminContact.status === "none"
              ? "No organization admin or owner could be found. You can still send the email and add a recipient."
              : "Could not load organization admins. You can still send the email and add a recipient."}
        </p>
      ) : null}
    </Modal>
  );
}
